'use client';

import { useEffect, useMemo, useRef, useState } from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';

import { useWindowVirtualizer } from '@tanstack/react-virtual';

import { usePostHog } from '@posthog/react';

import useResizeObserver from 'use-resize-observer';

import useCustomTooltip from './custom-tooltip';
import { queryFunctions } from '@hub/common/src/query-functions';

import { type Entry, FolderLayout } from '@hub/common/src/types/cit-public-api';

import { useCitOriginClient } from './CitOriginClientProvider';
import { useCookieConsentStatus } from './CookieConsentStatusProvider';
import { trackSearch } from './PostHogAnalyticsProvider';

import EntryCard from './EntryCard';
import { useCitThemeClient } from './CitThemeProvider';

const OVERSCAN_MULTIPLIER = 2; // virtualize 'n' additional rows above & 'n' additional rows below the viewport
const MIN_COLUMNS = 2;
// padding applied to all sides of each entry card; reserved by the virtualizer so it never eats into the image/gap
const CARD_PADDING = 5;

interface ScrolledEntriesDisplayProps {
  accountId?: string;
  folderId?: string;
  classificationId?: string;
  searchQuery: string | null;
}

function PaginatedEntriesDisplay({
  accountId,
  folderId,
  classificationId,
  searchQuery
}: ScrolledEntriesDisplayProps) {
  const citOrigin = useCitOriginClient();

  const posthog = usePostHog();

  const { consentLoaded } = useCookieConsentStatus();

  const { accountTheme } = useCitThemeClient();

  const { renderSlot1, renderSlot2, renderSlot3, renderSlot4 } =
    accountTheme.getRenderedThumbnailSlots();

  const isGrid = accountTheme.folderLayout() === FolderLayout.GRID;

  const [columnCount, setColumnCount] = useState(isGrid ? 8 : 1);

  const wantedImageSize = accountTheme.thumbnailSize();
  const [imageSize, setImageSize] = useState<number>(wantedImageSize);

  // the gap feeds column/offset math here, so it has to stay a number
  const gap = accountTheme.gap(false);

  const { customTooltip, handleHideToolTip, handleShowToolTip } = useCustomTooltip();

  const [textBoxHeight, setTextBoxHeight] = useState(0);

  const listRef = useRef<HTMLDivElement>(null);
  const textBoxRef = useRef<HTMLDivElement>(null);

  const { data, isError, isLoading, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: [
        'paginated-entries',
        {
          accountId,
          folderId,
          classificationId,
          searchQuery,
          citOrigin
        }
      ],
      initialPageParam: undefined,
      getNextPageParam: (lastPage: { entries: Entry[]; total: number; cursor?: string }) => {
        // Only return cursor if we have entries and a valid cursor
        if (lastPage.entries?.length > 0 && lastPage.cursor) {
          return lastPage.cursor;
        }
        return undefined;
      },
      queryFn: queryFunctions.fetchEntriesWithPagination
    });

  const mediaAddornment = accountTheme.mediaAdornment();

  const allEntries = useMemo(() => (data ? data.pages.flatMap((d) => d.entries) : []), [data]);

  const { width: containerWidth = 0 } = useResizeObserver<HTMLDivElement>({
    ref: listRef.current
  });

  // used over just a normal ref because of its ability to detect when the browswer font size changes
  const { height: observedTextBoxHeight = 0 } = useResizeObserver<HTMLDivElement>({
    ref: textBoxRef.current
  });

  // fire search analytics once per query after results settle. classification lists have no account, and
  // the dashboards are per account, so their searches are not reported
  useEffect(() => {
    // PostHogSyncProperties mounts once consent loads and sets the account group before this runs
    if (!searchQuery || !accountId || !consentLoaded) {
      return;
    }

    if (isLoading || isFetching) {
      return;
    }

    trackSearch(
      posthog,
      { accountId, folderId, classificationId },
      searchQuery,
      !!data?.pages[0]?.entries.length
    );
  }, [
    searchQuery,
    accountId,
    folderId,
    classificationId,
    isLoading,
    isFetching,
    data,
    posthog,
    consentLoaded
  ]);

  const virtualizer = useWindowVirtualizer({
    count: hasNextPage ? allEntries.length + 1 : allEntries.length,
    estimateSize: () => imageSize + 2 * CARD_PADDING + gap + (isGrid ? textBoxHeight : 0),
    overscan: columnCount * OVERSCAN_MULTIPLIER,
    ...(isGrid ? { lanes: columnCount } : {})
  });

  useEffect(() => {
    // automatically scroll back to the top when a new search is made
    virtualizer.scrollToIndex(0);
  }, [searchQuery, virtualizer]);

  const virtualItems = virtualizer.getVirtualItems();

  useEffect(() => {
    if (containerWidth > 0) {
      // Calculate available width accounting for padding
      const availableWidth = containerWidth;
      // each tile is the image plus the card padding on both sides
      const tileSize = wantedImageSize + 2 * CARD_PADDING;
      // Calculate how many columns can fit including gaps
      const cols = Math.max(MIN_COLUMNS, Math.floor((availableWidth + gap) / (tileSize + gap)));
      // Calculate the actual tile width to fill space evenly
      const tileWidth = Math.floor((availableWidth - (cols - 1) * gap) / cols);

      setColumnCount(isGrid ? cols : 1);
      // imageSize is the image dimension; the padding lives outside it within the tile
      setImageSize(tileWidth - 2 * CARD_PADDING);

      setTextBoxHeight(observedTextBoxHeight);

      virtualizer.measure();
    }
  }, [containerWidth, virtualizer, observedTextBoxHeight, gap, wantedImageSize, isGrid]);

  useEffect(() => {
    const [lastItem] = [...virtualItems].reverse();

    if (!lastItem) {
      return;
    }

    if (lastItem.index >= allEntries.length - 1 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, fetchNextPage, isFetchingNextPage, virtualItems, allEntries]);

  // scroll to the top of the page when the folderId changes or navigated to from a different page, Link component is supposed to do this automatically but it doesn't for some reason
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [folderId]);

  const bodyFont = accountTheme.bodyFont();
  const lineHeight = accountTheme.lineHeight();
  const letterSpacing = accountTheme.letterSpacing();

  return (
    <>
      {customTooltip}
      {searchQuery ? (
        <div className='flex items-center h-16'>
          <h1 className='text-xl font-semibold'>Search results for &quot;{searchQuery}&quot;</h1>
        </div>
      ) : null}

      <div ref={listRef} style={{ display: 'flex', justifyContent: 'center' }}>
        <div
          style={{
            position: 'relative',
            height: `${virtualizer.getTotalSize()}px`,
            ...(isGrid
              ? {
                  width: `${
                    columnCount * (imageSize + 2 * CARD_PADDING) + (columnCount - 1) * gap
                  }px`
                }
              : { width: '100%' }),
            minHeight: '100px'
          }}
        >
          {virtualItems.map((virtualItem) => {
            const entry = allEntries[virtualItem.index];
            // Skip rendering if we don't have an entry and we're not loading more
            if (!entry) {
              return null;
            }

            const col = virtualItem.index % columnCount;

            return (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'absolute',
                  // need to calculate x position ourselves, y position is handled by virtualizer
                  ...(isGrid
                    ? {
                        transform: `translate(${
                          col * (imageSize + 2 * CARD_PADDING + gap)
                        }px, ${virtualItem.start}px)`,
                        width: `${imageSize + 2 * CARD_PADDING}px`,
                        height: `${imageSize + textBoxHeight + 2 * CARD_PADDING}px`
                      }
                    : {
                        transform: `translateY(${virtualItem.start}px)`,
                        width: '100%',
                        height: `${imageSize + 2 * CARD_PADDING}px`
                      })
                }}
                key={virtualItem.key}
                data-index={virtualItem.index}
                className='entry'
              >
                <EntryCard
                  classificationId={classificationId}
                  folderId={folderId}
                  entry={entry}
                  searchQuery={searchQuery}
                  handleHideToolTip={handleHideToolTip}
                  handleShowToolTip={handleShowToolTip}
                  imageSize={imageSize}
                  textBoxHeight={textBoxHeight}
                  mediaAddornment={mediaAddornment}
                  cardPadding={CARD_PADDING}
                  entryCardColor={accountTheme.entryCardColor()}
                />
              </div>
            );
          })}
          {/* show a loading symbol if theres a next page and we're fetching it */}
          {/* TODO: replace 'Loading...' with an actual loading symbol */}
          {hasNextPage && isFetchingNextPage && !isError ? (
            <div
              style={{
                position: 'absolute',
                width: '100%',
                textAlign: 'center',
                // display in the center of where last row of entries would be
                transform: `translateY(${
                  virtualItems[virtualItems.length - 1].start +
                  (imageSize + textBoxHeight + 2 * CARD_PADDING) / 2
                }px)`,
                fontFamily: bodyFont,
                lineHeight,
                letterSpacing
              }}
            >
              Loading...
            </div>
          ) : null}
          {isError ? (
            <div
              style={{
                position: 'absolute',
                width: '100%',
                textAlign: 'center',
                // display in the center of where last row of entries would be
                transform: `translateY(${
                  virtualItems[virtualItems.length - 1].start +
                  (imageSize + textBoxHeight + 2 * CARD_PADDING) / 2
                }px)`,
                fontFamily: bodyFont,
                lineHeight,
                letterSpacing
              }}
            >
              Something went wrong...
            </div>
          ) : null}
        </div>
      </div>
      {/*  hidden copy of entry slot div for measuring text box size */}
      <div
        ref={textBoxRef}
        style={{
          position: 'absolute',
          left: '-9999px',
          visibility: 'hidden',
          width: `${imageSize}px`
        }}
        aria-hidden='true'
      >
        {renderSlot1 ? (
          <h6 className='slot1' style={{ fontFamily: bodyFont, lineHeight, letterSpacing }}>
            slot1
          </h6>
        ) : undefined}
        {renderSlot2 ? (
          <p className='slot2' style={{ fontFamily: bodyFont, lineHeight, letterSpacing }}>
            slot2
          </p>
        ) : undefined}
        {renderSlot3 ? (
          <p className='slot3' style={{ fontFamily: bodyFont, lineHeight, letterSpacing }}>
            slot3
          </p>
        ) : undefined}
        {renderSlot4 ? (
          <p className='slot4' style={{ fontFamily: bodyFont, lineHeight, letterSpacing }}>
            slot4
          </p>
        ) : undefined}
      </div>
    </>
  );
}

export default function ScrolledEntriesDisplay(props: ScrolledEntriesDisplayProps) {
  return (
    <>
      {/* <ReactQueryDevtools initialIsOpen={false} /> */}
      <PaginatedEntriesDisplay {...props} />
    </>
  );
}
